/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactSVG } from 'react-svg';
import {
  Button,
  PortalModal,
  VolumeDiscounts,
  useParameters,
} from '@web/utils/src';
import plusIcon from '@web/utils/src/assets/icons/functions/plus-icon.svg';
import { getCatalogueImage } from '@web/utils/src/components/ArticleImage/imageHelpers';
import { AmountPicker } from '@/components/AmountPicker';
import { LoosePicker } from '@/components/LoosePicker';
import { env } from '@/env';
import useGetInventoryItem from '@/hooks/useGetInventoryItem';
import { useAppSelector } from '@/store/hooks';
import { ArticleData, CartItem, CartItemData } from '@agrantis/cart';
import { ItemType, getItemType } from '@agrantis/cart/itemType';

type Props = {
  cartItem: CartItem | null;
  modalVisible: boolean;
  setModalVisible: (boolean) => void;
  addItemToCart: (data: {
    itemData: CartItemData;
    amount: number;
    price?: number;
  }) => void;
  isAdjustAmount?: boolean;
  inventoryItem?: ArticleData | null;
};

const ItemDetailsModal = ({
  cartItem,
  modalVisible,
  setModalVisible,
  addItemToCart,
  isAdjustAmount = false,
  inventoryItem,
}: Props) => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState(0);
  const { retailerId } = useParameters<{ retailerId: string }>();

  const { inventoryItem: inventoryItemWithPrice, fetching } =
    useGetInventoryItem(cartItem?.itemData?.id.toString(), retailerId);

  const settings = useAppSelector((state) => state.retailer.retailer?.settings);
  const isVolumeDiscountsEnabled = !settings?.hideVolumeDiscounts;

  const itemType =
    cartItem &&
    getItemType({
      loose: cartItem.itemData.loose!,
      quantity: cartItem.itemData.quantity!,
      unit: cartItem.itemData.unit,
    });

  const handleAddToCart = () => {
    if (!cartItem) return;
    if (!amount || amount < 1) {
      alert(
        t('checkout.amount_has_to_be', {
          value: `${cartItem.itemData.quantity}${cartItem.itemData.unit}`,
        } as any),
      );
      return;
    }

    addItemToCart({
      itemData: cartItem.itemData,
      amount,
      price: cartItem.price ?? undefined,
    });
    setModalVisible(false);
  };

  useEffect(() => {
    if (cartItem?.amount) setAmount(cartItem.amount);
  }, [cartItem]);

  const image = getCatalogueImage({
    env,
    image: inventoryItem?.image ?? '',
    taxonomyId: inventoryItem?.taxonomy?.id || null,
  });

  const showVolumeDiscounts = useMemo(() => {
    const item = cartItem?.itemData;
    if (!item) return false;
    if (item?.hasValidIndividualCustomerPrice) return false;
    if (item?.hasLivePrice) return false;
    if (item?.volumeDiscounts?.length === 0) return false;
    if (!isVolumeDiscountsEnabled) return false;
    return true;
  }, [cartItem, isVolumeDiscountsEnabled]);

  if (!cartItem || !inventoryItem || fetching) return null;

  return (
    <PortalModal isOpen={modalVisible} setIsOpen={() => setModalVisible(false)}>
      <div className="w-full max-w-[400px] bg-white rounded-16-0 shadow flex flex-col p-2-0 relative">
        <button
          onClick={() => setModalVisible(false)}
          className="p-0-5 rotate-45 absolute right-1-0 top-1-0 cursor-pointer"
        >
          <ReactSVG data-testid="close" src={plusIcon} />
        </button>

        {image && (
          <img
            src={image}
            alt={inventoryItem?.name}
            className="w-full h-48 object-contain my-1-2 rounded-base"
          />
        )}
        <div className="space-y-1-0">
          <p className="text-1-4 text-gray-600 font-light">
            {inventoryItem?.subtitle}
          </p>
          <h2 className="text-2-0 font-bold text-gray-900">
            {inventoryItem?.name}
          </h2>
          <p className="text-1-2 text-gray-700">{inventoryItem?.description}</p>
        </div>
        {showVolumeDiscounts && (
          <div className="my-3-0">
            <VolumeDiscounts
              volumeDiscounts={cartItem.itemData.volumeDiscounts ?? []}
              unit={cartItem.itemData.unit}
              amount={inventoryItemWithPrice?.retailerBasePrice?.amount ?? 0}
            />
          </div>
        )}
        {!!cartItem.price && (
          <div className="flex my-3-0 justify-between">
            <div className="text-body1 font-bd">{t('checkout.cost')}:</div>
            <p className="text-body1">
              {`${cartItem.price?.toString()} €/ ${
                cartItem.itemData.quantity
              } ${cartItem.itemData.unit}`}
            </p>
          </div>
        )}
        <div className="mt-2-0">
          {itemType === ItemType.Loose ? (
            <LoosePicker
              cartItem={cartItem}
              onAmountChange={(e) => setAmount(e.amount)}
            />
          ) : (
            <AmountPicker
              cartItem={cartItem}
              onAmountChange={(e) => setAmount(e.amount)}
            />
          )}
        </div>

        <div className="h-px bg-gray-200 my-1-6" />

        <div className="space-y-1-0">
          <Button onClick={handleAddToCart} className="w-full">
            {isAdjustAmount
              ? t('checkout.adjust_amount')
              : t('checkout.add_to_cart')}
          </Button>
        </div>
      </div>
    </PortalModal>
  );
};

export default ItemDetailsModal;
